<template>
  <div class="ap-sidebar">
    <div
      v-if="options.module"
      class="ap-sidebar__header"
    >
      <b-row class="py-2 w-100 m-0">
        <b-col
          cols="9"
          class="d-flex pl-3 pr-0"
        >
          <div class="align-self-center">
            <div>
              <span
                class="ap-sidebar__title font-weight--medium m-0"
              >
                {{ $tc(options.module.name) }}
              </span>
            </div>
          </div>
        </b-col>
        <b-col class="d-flex align-items-center justify-content-end pr-2">
          <div
            :qa="`expanded-sidebar-${options.module.name}`"
            class="ap-sidebar__main-icon"
          >
            <fa-icon
              fixed-width
              :icon="options.module.icon"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <div
      class="scroll-sidebar"
      qa="expanded-sidebar-group-container"
    >
      <section-visibility-checks
        v-if="viewAllAllowed"
        :is-incidents="isIncidents"
        :is-grossing="isGrossing"
        :is-diagnosis="isDiagnosis"
        :view-all-enabled="viewAllEnabled"
        @view-all-toggled="$emit('view-all-toggled', $event)"
      />

      <section-visibility-responsibilities
        v-if="isDiagnosis && sidebarConfigLoaded"
        :sidebar-store="store"
        @update-responsibilities="$emit('update-responsibilities', $event)"
      />

      <section-parent-checks
        v-if="isDiagnosis"
        :sidebar-store="store"
        :popover="false"
      />

      <b-nav
        class="ap-sidebar__block"
        vertical
      >
        <div
          v-for="option in options.expanded"
          :key="option.id"
        >
          <template v-if="!option.hide">
            <div class="ap-sidebar__section p-2 text-center main">
              <b-col>{{ $tc(customLabelOption(option, labelOption)) }}</b-col>
            </div>
            <div v-if="option.hasChildren">
              <div
                v-for="subOption in option.children"
                :key="subOption.id"
                class="ap-sidebar__item"
              >
                <template v-if="!subOption.hide">
                  <div v-if="!subOption.hasChildren">
                    <option-nav
                      :key="subOption.id"
                      v-model="selected"
                      :icon="subOption.icon"
                      :alticon="subOption.alticon"
                      :store="store"
                      :option="subOption"
                      :default-option="defaultOption"
                      @input="setParent(option)"
                      @setDefaultOption="setDefaultOption($event)"
                    >
                      <span class="link-label">
                        {{ $tc(customLabelSubOption(subOption, labelSubOption)) }}
                      </span>
                      <b-badge
                        v-if="counters && counters[subOption.id]"
                        class="ap-sidebar__badge"
                        variant="vt-red"
                        pill
                      >
                        {{ counters[subOption.id] }}
                      </b-badge>
                    </option-nav>
                  </div>
                  <sub-menu
                    v-if="subOption.hasChildren"
                    :id="subOption.id"
                    :alticon="subOption.alticon"
                    :title="$tc(subOption.label)"
                    :icon="subOption.icon"
                  >
                    <option-nav
                      v-for="subSubOption in subOption.children"
                      :key="subSubOption.id"
                      v-model="selected"
                      :class="{ lineHeight: subOption.noLock}"
                      class-link="submenu-links"
                      :no-lock="subOption.noLock"
                      :store="store"
                      :option="subSubOption"
                      :default-option="defaultOption"
                      @input="setParent(subOption)"
                      @setDefaultOption="setDefaultOption($event)"
                    >
                      <template v-if="!subSubOption.hide">
                        <span>{{ $tc(customLabelSubSubOption(subSubOption, labelSubSubOption)) }}</span>
                        <b-badge
                          v-if="counters && counters[subSubOption.id]"
                          class="ap-sidebar__badge"
                          variant="vt-red"
                          pill
                        >
                          {{ counters[subSubOption.id] }}
                        </b-badge>
                      </template>
                    </option-nav>
                  </sub-menu>
                </template>
              </div>
            </div>
          </template>
        </div>
      </b-nav>

      <sidebar-filters-time-filter-expanded
        v-if="showTimeFilters"
        :selected-option.sync="selectedTimeFilter"
        :default-option="timeFilterDefaultOption"
        :options="timeFilterOptions"
        @update:selected-option="selectedTimeFilter = $event"
      />

      <sidebar-filters-custom-filter-expanded
        v-if="showCustomFilters"
        :selected-option.sync="selected"
        :default-option="defaultOption"
        :options="customOptions"
        :label="labelCustomOption"
        :custom-label="customLabelCustomOption"
        :store="store"
        :disable-add-filter="disableAddFilter"
        @create="$emit('click-add-filter')"
        @rename="$emit('rename-option', $event)"
        @remove="$emit('remove-option', $event)"
        @update="$emit('update-option', $event)"
        @selected-parent-option="setParent()"
        @set-default-option-parent="setDefaultOption($event)"
        @update:selected-option="selected = $event"
      />
    </div>
    <b-nav
      class="ap-sidebar__footer"
      vertical
    >
      <b-nav-item
        id="expandedSideBarCollapse"
        class="w-100"
        @click="$emit('contract')"
      >
        <b-button
          v-shortkey="['f1']"
          qa="expanded-sidebar-compact-button"
          class="blank-button"
          @shortkey.prevent="$emit('contract')"
        >
          <i
            class="fas fa-chevron-left px-3"
            aria-hidden="true"
          />
        </b-button>
      </b-nav-item>
    </b-nav>
  </div>
</template>

<script>
import SubMenu from '@/components/sideBar/SubMenu.vue'
import SectionParentChecks from '@/modules/diagnosisModule/sideBar/diagnosisSidebarChecks/SectionParentChecks.vue'
import SectionVisibilityChecks from '@/modules/diagnosisModule/sideBar/sidebarVisibilityChecks/SectionVisibilityChecks.vue'
import SectionVisibilityResponsibilities from '@/modules/diagnosisModule/sideBar/diagnosisSidebarChecks/SectionVisibilityResponsibilities.vue'
import OptionNav from '@/components/sideBar/OptionNav.vue'
import SidebarFiltersTimeFilterExpanded from '@/components/sideBar/filters/time-filter/expanded/sidebar-filters-time-filter-expanded.vue'
import SidebarFiltersCustomFilterExpanded from '@/components/sideBar/filters/custom-filter/expanded/sidebar-filters-custom-filter-expanded.vue'

export default {
    name: 'ExpandedSideBar',
    components: {
        SubMenu,
        OptionNav,
        SectionParentChecks,
        SectionVisibilityChecks,
        SectionVisibilityResponsibilities,
        SidebarFiltersTimeFilterExpanded,
        SidebarFiltersCustomFilterExpanded
    },
    props: {
        notRead: { type: Number },
        drafts: { type: Number },
        issues: { type: Number },
        options: { type: Object },
        customOptions: { type: Array, default: () => [] },
        counters: { type: Object },
        selectedOption: { type: Object, default: null },
        defaultOption: { type: Object, default: null },
        disableAddFilter: { type: Boolean, default: false },
        store: { type: Object },
        isDiagnosis: { type: Boolean },
        isGrossing: { type: Boolean },
        isIncidents: { type: Boolean },
        viewAllAllowed: { type: Boolean, default: false },
        viewAllEnabled: { type: Boolean, default: false },
        labelOption: { type: String, default: 'label' },
        customLabelOption: {
            type: Function,
            default(option, label) {
                if (option) {
                    return label ? option[label] : option
                }
                return ''
            }
        },
        labelSubOption: { type: String, default: 'label' },
        customLabelSubOption: {
            type: Function,
            default(option, label) {
                if (option) {
                    return label ? option[label] : option
                }
                return ''
            }
        },
        labelSubSubOption: { type: String, default: 'label' },
        customLabelSubSubOption: {
            type: Function,
            default(option, label) {
                if (option) {
                    return label ? option[label] : option
                }
                return ''
            }
        },
        // custom filter
        showCustomFilters: { type: Boolean, default: true },
        labelCustomOption: { type: String },
        customLabelCustomOption: { type: Function },
        // time filter
        showTimeFilters: { type: Boolean, default: false },
        timeFilterSelectedOption: { type: String },
        timeFilterDefaultOption: { type: String },
        timeFilterOptions: { type: Array }
    },
    computed: {
        selected: {
            set(value) {
                this.$emit('update:selected-option', value)
            },
            get() {
                return this.selectedOption
            }
        },
        selectedTimeFilter: {
            set(value) {
                this.$emit('update:time-filter-selected-option', value)
            },
            get() {
                return this.timeFilterSelectedOption
            }
        },
        sidebarConfigLoaded() {
            return this.store?.loadedConfig
        }
    },
    methods: {
        setParent(parentOption) {
            this.$emit('selected-parent-option', parentOption)
        },
        setDefaultOption(ev) {
            this.$emit('setDefaultOptionParent', ev)
        }
    }
}
</script>

<style>
.section-parent-checks .check-parent-compact{
  display: none;
}

.sr-side-bar .check-popover{
  display: none;
}

.sr-side-bar .nav-link:not(:last-child) {
  min-height: 37px;
}

.lab-submenu .selector{
  display: none;
}

</style>

<style scoped lang="scss">
.blank-button{
  padding: 0;
  margin: 0;
  background: transparent;
  border: transparent;
  font-size: font-size(small);
}

.sr-side-bar{
-webkit-box-shadow: 1px 0 5px 0 vt-color(sidebar-box-shadow);
-moz-box-shadow: 1px 0 5px 0 vt-color(sidebar-box-shadow);
box-shadow: 1px 0 5px 0 vt-color(sidebar-box-shadow);
}

.submenu-links {
  .nav-link {
    padding-left:4rem;
  }
}

.count-badge{
  font-size:0.7rem !important;
  text-align:right;
}

.section-header{
  text-transform: uppercase;
  font-size:0.9rem;
  background: vt-color(sidebar-section-header-bg);
  color: vt-color(sidebar-section-header-text);
}

.menu-main-title h4{
  font-size:1.1rem;
  font-weight: normal;
}

.menu-main-title small{
  font-size:0.7rem;
  text-transform: uppercase;
}

.menu-main-title{
  line-height: 0.9rem;
}

.menu-module {
  display: flex;
  align-items: center;
}

.section-pd{
  width:100%;
  margin:0;
}

.icon-wrapper-main{
  font-size:1.6rem;
}

.selector:hover{
  background: vt-color(sidebar-single-element-hover);
}

.icon-wrapper {
  font-size: 1rem;
  margin-right: 7px;
  width: 32px;
  color: vt-color(white-light);
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  > .svg-inline--fa {
    margin-left: unset !important;
  }
}

.nav-link span{
    font-size: 0.9rem;
  }

.link-label{
  margin-right: auto;
}

.sr-side-bar {
  display: flex;
  flex-direction: column;
  width: 218px;
  height: 100%;
  min-height: 0;
    ::v-deep .nav-link {
    box-sizing: border-box;
    display: flex;
    padding-right: .2rem;
    align-items: center;
    color: $base-text-color;
    color: vt-color(sidebar-child-font) !important;
    font-weight:normal;
  }
}

.bottom-menu {
  flex: 0 0 auto;
  background: vt-color(sidebar-bottom-menu-bg);
  height: 31px;
}

.bottom-menu a{
  height: 1rem;
  padding-top: 1rem;
}

.scroll-sidebar {
  overflow-y: auto;
  flex-grow: 1;
}

.bottom-menu:hover{
  background: vt-color(sidebar-bottom-menu-hover);
}

.bottom-menu .blank-button {
  font-size: 1rem;
  background: transparent !important;
}

.count-badge {
  margin-left: auto;
}

@media (max-width: 1300px){
  .count-badge{
    font-size:0.7rem !important;
  }

  .link-label{
    font-size: 0.9rem;
  }

  .icon-wrapper{
    font-size: 1.1rem;
  }
  .section-header{
    font-size:0.8rem;
  }

  .menu-main-title h4{
    font-size:1rem;
  }

  .menu-main-title small{
    font-size:0.65rem;
  }

  .menu-main-title{
    line-height: 0.9rem;
  }

  .icon-wrapper-main{
  font-size:1.4rem;
}

  .nav-link span{
    font-size: 0.9rem;
  }
}

</style>
